// Filter bar of the data-table component (config.filters[]). Pure helpers + DOM builder; the controller owns the
// state object and the refetch. DOM only through createElement / textContent / setAttribute — never innerHTML.
//
// filters[] item: { key, label, type, options?, param?, params?, allLabel?, placeholder?, default? }
//   segment   one choice among options, first button is the "all" state (allLabel, default "Wszystkie"); `param`
//             (default key) is the query parameter, an empty value is not sent. `default` (an option value) is the
//             state of a page opened without that parameter; with syncUrl the "all" state is then written as
//             `param=` so a reload keeps it. The reset button always goes to "all".
//   chips     several options at once -> the parameter is repeated: ?status=paid&status=failed
//   daterange two <input type="date"> -> params {from, to}, e.g. {"from": "created_from", "to": "created_to"};
//             a side without a mapped parameter is not rendered (params {"from": "date_from"} = one „Od" input)
//   combobox  one pick out of options via the shared `combobox` controller (config options, no remote search)
// options: [{ value, label }]. State shape per type: segment/combobox -> string ("" = none), chips -> string[],
// daterange -> { from: string, to: string }.
//
// TODO (extension points, intentionally not built): remote combobox options (filter.url), single-select chips,
// numeric ranges, per-filter visibility rules.

export const FILTER_TYPES = ["segment", "chips", "daterange", "combobox"];

let uid = 0;

const paramName = (f) => f.param || f.key;

export function emptyState(filters) {
  const state = {};
  for (const f of filters) {
    if (f.type === "chips") state[f.key] = [];
    else if (f.type === "daterange") state[f.key] = { from: "", to: "" };
    else state[f.key] = "";
  }
  return state;
}

// Empty state + each filter's `default` (a page opened without any parameter).
export function initialState(filters) {
  const state = emptyState(filters);
  for (const f of filters) {
    if (f.default !== undefined && f.default !== null) state[f.key] = String(f.default);
  }
  return state;
}

// Browser query string -> state (unknown values for option-based filters are dropped; an absent parameter keeps the default).
export function stateFromSearch(filters, params) {
  const state = initialState(filters);
  const known = (f, v) => !f.options || f.options.some((o) => String(o.value) === v);
  for (const f of filters) {
    if (f.type === "chips") {
      state[f.key] = params.getAll(paramName(f)).filter((v) => known(f, v));
    } else if (f.type === "daterange") {
      state[f.key] = { from: params.get(f.params?.from) || "", to: params.get(f.params?.to) || "" };
    } else if (params.has(paramName(f))) {
      const v = params.get(paramName(f)) || "";
      state[f.key] = known(f, v) ? v : "";
    }
  }
  return state;
}

// state -> query params (appended to a URLSearchParams; chips repeat the key, empty values are skipped).
// `explicitEmpty` (browser URL only) writes `param=` for the "all" state of a filter that has a `default`.
export function appendParams(filters, state, params, { explicitEmpty = false } = {}) {
  for (const f of filters) {
    const value = state[f.key];
    if (f.type === "chips") {
      for (const v of value || []) params.append(paramName(f), v);
    } else if (f.type === "daterange") {
      if (value?.from && f.params?.from) params.set(f.params.from, value.from);
      if (value?.to && f.params?.to) params.set(f.params.to, value.to);
    } else if (value) {
      params.set(paramName(f), value);
    } else if (explicitEmpty && f.default) {
      params.set(paramName(f), "");
    }
  }
}

// Names of every query parameter the filters own (the URL sync removes them before writing the new state).
export function ownedParams(filters) {
  const names = [];
  for (const f of filters) {
    if (f.type === "daterange") names.push(f.params?.from, f.params?.to);
    else names.push(paramName(f));
  }
  return names.filter(Boolean);
}

export function hasActive(filters, state) {
  return filters.some((f) => {
    const v = state[f.key];
    if (f.type === "chips") return v.length > 0;
    if (f.type === "daterange") return Boolean(v.from || v.to);
    return Boolean(v);
  });
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function segment(f, state, changed) {
  const row = el("div", "ls-radio-row");
  row.setAttribute("role", "radiogroup");
  row.setAttribute("aria-label", f.label);
  const name = `dt-${f.key}-${(uid += 1)}`;
  const options = [{ value: "", label: f.allLabel || "Wszystkie" }, ...(f.options || [])];
  for (const opt of options) {
    const label = el("label");
    const input = el("input");
    input.type = "radio";
    input.name = name;
    input.value = String(opt.value);
    input.checked = state[f.key] === String(opt.value);
    input.addEventListener("change", () => {
      state[f.key] = input.value;
      changed();
    });
    label.append(input, document.createTextNode(opt.label));
    row.append(label);
  }
  return row;
}

function chips(f, state, changed) {
  const group = el("div", "ls-dt__chips");
  group.setAttribute("role", "group");
  group.setAttribute("aria-label", f.label);
  for (const opt of f.options || []) {
    const value = String(opt.value);
    const btn = el("button", "ls-dt__chip", opt.label);
    btn.type = "button";
    btn.dataset.value = value;
    const paint = () => {
      const on = state[f.key].includes(value);
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    };
    paint();
    btn.addEventListener("click", () => {
      const list = state[f.key];
      state[f.key] = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      paint();
      changed();
    });
    group.append(btn);
  }
  return group;
}

function daterange(f, state, changed) {
  const wrap = el("div", "ls-dt__dates");
  for (const [side, text] of [["from", "Od"], ["to", "Do"]]) {
    if (!f.params?.[side]) continue; // an open-ended range (only `from` mapped) shows one input
    const label = el("label", "ls-dt__date");
    const input = el("input", "ls-input ls-input--form");
    input.type = "date";
    input.value = state[f.key][side];
    input.setAttribute("aria-label", `${f.label}: ${text}`);
    input.dataset.side = side;
    input.addEventListener("change", () => {
      state[f.key] = { ...state[f.key], [side]: input.value };
      changed();
    });
    label.append(el("span", "ls-dt__date-cap", text), input);
    wrap.append(label);
  }
  return wrap;
}

// Same DOM as templates/partials/_combobox.html (single mode, local options); the `combobox` controller
// connects to it as soon as it is attached.
function combobox(f, state) {
  const id = `combo-dt-${f.key}-${(uid += 1)}`;
  const root = el("div", "ls-field ls-combo");
  root.dataset.controller = "combobox";
  root.setAttribute("data-combobox-name-value", f.key);
  root.setAttribute("data-combobox-placeholder-value", f.placeholder || "Szukaj…");
  root.setAttribute("data-combobox-options-value", JSON.stringify((f.options || []).map((o) => ({ value: o.value, label: o.label }))));
  const current = (f.options || []).find((o) => String(o.value) === state[f.key]);
  if (current) root.setAttribute("data-combobox-initial-value", JSON.stringify([{ value: current.value, label: current.label }]));
  const label = el("label", "ls-label ls-visually-hidden", f.label);
  label.htmlFor = id;
  const box = el("div", "ls-combo__box");
  box.setAttribute("data-combobox-target", "box");
  box.setAttribute("data-action", "click->combobox#focusInput");
  const chipsSlot = el("span", "ls-combo__chips");
  chipsSlot.setAttribute("data-combobox-target", "chips");
  const input = el("input", "ls-combo__input");
  input.id = id;
  input.type = "text";
  input.autocomplete = "off";
  input.setAttribute("data-combobox-target", "input");
  input.setAttribute("data-action", "input->combobox#search focus->combobox#open blur->combobox#close keydown->combobox#keydown");
  const caret = el("span", "ls-combo__caret", "▼");
  caret.setAttribute("aria-hidden", "true");
  box.append(chipsSlot, input, caret);
  const list = el("div", "ls-combo__list");
  list.setAttribute("data-combobox-target", "list");
  list.hidden = true;
  const hidden = el("span");
  hidden.setAttribute("data-combobox-target", "hidden");
  root.append(label, box, list, hidden);
  return root;
}

const BUILDERS = { segment, chips, daterange, combobox };

// -> the whole bar. `state` is mutated in place; `onChange()` runs after every user change, `onReset()` on
// „Wyczyść filtry" (the controller then re-renders the bar from an empty state).
export function buildFilterBar(filters, state, { onChange, onReset }) {
  const bar = el("div", "ls-dt__filters-row");
  const reset = el("button", "ls-btn ls-btn--ghost ls-btn--sm ls-dt__reset", "Wyczyść filtry");
  reset.type = "button";
  reset.hidden = !hasActive(filters, state);
  const changed = () => {
    reset.hidden = !hasActive(filters, state);
    onChange();
  };
  for (const f of filters) {
    const build = BUILDERS[f.type];
    if (!build) continue;
    const box = el("div", "ls-dt__filter");
    box.dataset.filter = f.key;
    box.append(el("span", "ls-dt__filter-label", f.label), build(f, state, changed));
    bar.append(box);
  }
  reset.addEventListener("click", onReset);
  bar.append(reset);
  return bar;
}
